import { motion } from "framer-motion";
import { Star, ArrowUpRight, BadgeCheck, ExternalLink } from "lucide-react";
import { REVIEWS, WRITE_REVIEW_URL, READ_ALL_REVIEWS_URL } from "../data/site";
import { useRatingFigures } from "./LiveRatingProvider";

const Stars = ({ size = 14, rating = 5 }) => (
  <span className="flex gap-0.5" aria-label={`${Math.round(rating)} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < Math.round(rating) ? "fill-orange-500 text-orange-500" : "text-slate-600"}
      />
    ))}
  </span>
);

export const Reviews = () => {
  const { isLive, rating, count, data: liveData } = useRatingFigures();
  const countLabel = `${count} Google reviews`;
  const cards = isLive ? liveData.reviews : REVIEWS;

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 lg:mb-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5 }}
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-500 font-semibold mb-4"
            >
              Word of mouth
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="reviews-heading"
              className="font-display font-bold uppercase tracking-tight text-slate-50 text-3xl sm:text-4xl lg:text-5xl leading-[0.95]"
            >
              Straight from Google.
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
          >
            <div
              data-testid="reviews-rating-badge"
              className="flex items-center gap-4 rounded-2xl border border-line bg-surface px-6 py-4"
            >
              <p className="font-display font-bold text-5xl text-orange-500 leading-none">
                {rating}
              </p>
              <div>
                <Stars rating={isLive ? liveData.rating : 5} />
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-2">
                  {countLabel}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-col items-start sm:items-start gap-4">
              <a
                href={READ_ALL_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="read-all-reviews-link"
                className="inline-flex items-center gap-2.5 h-12 px-6 rounded-full bg-orange-600 hover:bg-orange-500 text-ink font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(234,88,12,0.3)]"
              >
                <ExternalLink size={15} />
                Read all reviews on Google
              </a>
              <a
                href={WRITE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="write-review-link"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-orange-500 transition-colors"
              >
                <BadgeCheck size={15} />
                Write a Google review
                <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>

        <div
          data-testid={isLive ? "reviews-feed" : "reviews-fallback"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {cards.map((r, i) => (
            <motion.figure
              key={isLive ? `${r.author}-${i}` : i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={isLive ? `live-review-card-${i + 1}` : `review-card-${i + 1}`}
              className="rounded-2xl border border-line bg-surface p-6 lg:p-7 flex flex-col gap-5 transition-all duration-300 hover:border-orange-600/50 hover:-translate-y-1"
            >
              <Stars size={13} rating={isLive ? r.rating || 5 : 5} />
              <blockquote className="text-slate-300 leading-relaxed text-[15px] flex-1">
                "{r.text}"
              </blockquote>
              <figcaption className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-600 inline-block shrink-0" />
                  {isLive && r.authorUri ? (
                    <a
                      href={r.authorUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate hover:text-orange-500 transition-colors"
                    >
                      {r.author}
                    </a>
                  ) : (
                    <span className="truncate">{r.author}</span>
                  )}
                </span>
                {isLive && (r.relativeTime || r.googleMapsUri) ? (
                  <a
                    href={r.googleMapsUri || READ_ALL_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 hover:text-orange-500 transition-colors"
                  >
                    {r.relativeTime || "View on Google"}
                  </a>
                ) : null}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
          data-testid="reviews-attribution"
          className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600"
        >
          Reviews sourced from{" "}
          <a
            href={READ_ALL_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-orange-500 transition-colors"
          >
            Google
          </a>
        </motion.p>
      </div>
    </section>
  );
};
